import {
  ChangeEvent,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { attachmentDisplayUrl } from "../../Helper/AttachmentDisplayUrl";
import Communicator from "../../Helper/Communicator";
import { useComposerAttachStore } from "../../Helper/ComposerAttach";
import { prefersReducedMotion } from "../../Helper/CaptureFlight";
import {
  findCaptureResultComment,
  isCaptureResultImage,
  isCapturePageRecording,
} from "../../Helper/CaptureResult";
import { revealInScrollParent } from "../../Helper/ScrollHelper";
import {
  CaptureCardData,
  CaptureCardView,
  CaptureIssue,
  CaptureMediaKind,
  DEFAULT_CAPTURE_MAX_RECORDING_SEC,
  captureAcceptForKind,
  formatCaptureElapsed,
  formatCaptureFileSize,
  formatCaptureLimit,
  resolveCaptureCardView,
  resolveCaptureSupport,
} from "../../Helper/CaptureRequest";
import {
  CaptureStringKey,
  fillCaptureText,
  useCaptureText,
} from "../../Helper/CaptureStrings";
import {
  HOST_SCREENSHOT_MAX_AGE_MS,
  useCaptureStore,
} from "../../Stores/Capture";
import { useConfigStore } from "../../Stores/Config";
import { AppMode, useRouterStore } from "../../Stores/Router";
import { useTicketStore } from "../../Stores/Ticket";
import {
  AlertIcon,
  CameraIcon,
  CheckIcon,
  ImageFileIcon,
  InfoIcon,
  PaperclipIcon,
  PlayIcon,
  ScreenRecordIcon,
} from "../CaptureIcons/CaptureIcons";
import { MessageVideoPlayer } from "../MessageVideoPlayer/MessageVideoPlayer";
import "./CaptureRequestCard.scss";

interface CaptureRequestCardProps {
  card: CaptureCardData;
  /**
   * The request's customer-facing text. The message variant renders it in the
   * bubble above (ChatMessage), the workflow-step variant inside the card.
   */
  message?: string;
  variant?: "message" | "step";
  /** Conversation the card belongs to (forwarded to the host). */
  ticketShareToken?: string;
}

const ISSUE_TEXT: Record<CaptureIssue, CaptureStringKey> = {
  permissionDenied: "capturePermissionDenied",
  notSupported: "captureNotSupported",
  failed: "captureFailed",
  uploadTooLarge: "captureUploadTooLarge",
};

// The host takes this long to bring the widget back and settle it; the
// flight's target is measured after it.
const FLIGHT_SETTLE_MS = 260;
// No `capture-fly-done` by then (a host that lost the message): show the
// media anyway.
const FLIGHT_FALLBACK_MS = 1800;
// Same sizes as a chat message's image attachment.
const RESULT_THUMBNAIL_WIDTH = 800;
const RESULT_FULLSCREEN_WIDTH = 2048;

const Spinner = () => <span className="capture-card-spin" aria-hidden="true" />;

/**
 * The card under a capture request (contract §3, capture UI v2 —
 * design-handoffs/capture-ui): two tiles (Screenshot / Record screen), quiet
 * Upload / Not now links, a live status row while the host captures, and a
 * receipt with what was sent. Idle until tapped: no timers run unless a host
 * may still announce itself or a recording is live.
 */
export const CaptureRequestCard = ({
  card,
  message,
  variant = "message",
  ticketShareToken,
}: CaptureRequestCardProps) => {
  const t = useCaptureText();
  const local = useCaptureStore((state) => state.requests[card.id]);
  const hostCapabilities = useCaptureStore((state) => state.hostCapabilities);
  const legacyHost = useCaptureStore((state) => state.legacyHost);
  const activeRequestId = useCaptureStore((state) => state.activeRequestId);
  const hostScreenshot = useCaptureStore((state) => state.hostScreenshot);
  // "Take a new one": back to the regular tiles for this card.
  const [isHostShotDeclined, setHostShotDeclined] = useState(false);
  const openComposerPicker = useComposerAttachStore(
    (state) => state.openPicker,
  );
  const isChatbar = useRouterStore(
    (state) => state.appMode === AppMode.CHATBAR,
  );
  const isApp = useConfigStore((state) => state.isApp);
  // The customer's message with what was sent: the conversation folds it into
  // this card, so its file shows (and opens / plays) here. A workflow step's
  // card leaves the timeline, so its message stays.
  const result = useTicketStore((state) =>
    variant === "message"
      ? findCaptureResultComment(state.currentComments, card.id)
      : undefined,
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const slotRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastViewKey = useRef<string | undefined>(undefined);
  const [, setTick] = useState(0);

  const support = resolveCaptureSupport({
    capabilities: hostCapabilities,
    legacyHost,
    isChatbar,
  });

  // A capable host announces itself within a few seconds of boot. Until then
  // the capture tiles stay hidden (upload + skip still work); if nothing
  // arrives, the host is legacy and the card stays upload-only.
  useEffect(() => {
    if (support.resolved) {
      return;
    }
    const remaining = useCaptureStore.getState().markLegacyIfUnannounced();
    if (remaining <= 0) {
      return;
    }
    const timer = setTimeout(() => {
      useCaptureStore.getState().markLegacyIfUnannounced();
    }, remaining + 50);
    return () => clearTimeout(timer);
  }, [support.resolved]);

  // Back after a reload with the card still "in progress": nothing here works on it any more.
  useEffect(() => {
    useCaptureStore.getState().releaseStaleClaim(card);
    // Judged once per card on first sight (the store remembers it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id]);

  const view: CaptureCardView = resolveCaptureCardView({
    card,
    local,
    support,
    otherCaptureActive: !!activeRequestId && activeRequestId !== card.id,
  });
  const limitSec = card.maxDurationSec ?? DEFAULT_CAPTURE_MAX_RECORDING_SEC;

  // Live recording timer between the host's updates (paused: frozen).
  const isLiveRecording =
    view.type === "progress" &&
    view.label === "recording" &&
    !view.paused &&
    typeof view.elapsedAt === "number";
  useEffect(() => {
    if (!isLiveRecording) {
      return;
    }
    const interval = setInterval(() => setTick((tick) => tick + 1), 1000);
    return () => clearInterval(interval);
  }, [isLiveRecording]);

  // The sent result flies in (the host draws it across the page); the media
  // slot stays invisible until it lands.
  const isFlying = view.type === "sent" && view.flyPending === true;
  useEffect(() => {
    if (!isFlying) {
      return;
    }
    const finish = () => useCaptureStore.getState().finishFlight(card.id);
    const flight = useCaptureStore.getState().requests[card.id];
    // A workflow step's card leaves as soon as the workflow moves on, so it
    // is no place to land: it just fades in.
    if (
      variant === "step" ||
      useCaptureStore.getState().hostCapabilities?.flyIn !== true ||
      prefersReducedMotion()
    ) {
      finish();
      return;
    }
    const settle = setTimeout(() => {
      const slot = slotRef.current;
      if (!slot) {
        finish();
        return;
      }
      // Our own scroll container only (scrollIntoView would move the host
      // page on Safari).
      try {
        revealInScrollParent(slot);
      } catch (_) {}
      const rect = slot.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        finish();
        return;
      }
      Communicator.captureFly({
        requestId: card.id,
        to: {
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        },
        ...(flight?.flyFrom ? { from: flight.flyFrom } : {}),
        ...(flight?.flySrc ? { src: flight.flySrc } : {}),
        radius: 16,
      });
    }, FLIGHT_SETTLE_MS);
    const fallback = setTimeout(finish, FLIGHT_FALLBACK_MS);
    return () => {
      clearTimeout(settle);
      clearTimeout(fallback);
      // Unmounted mid-flight: never keep the conversation's scroll on hold.
      finish();
    };
  }, [isFlying, card.id, variant]);

  // A short cross-fade when the card changes state (not on first render).
  const viewKey =
    view.type === "progress"
      ? `progress:${view.label}`
      : view.type === "pending"
        ? "pending"
        : view.type;
  const swapped =
    lastViewKey.current !== undefined && lastViewKey.current !== viewKey;
  useLayoutEffect(() => {
    lastViewKey.current = viewKey;
  });

  const keepFocusInCard = () => {
    rootRef.current?.focus({ preventScroll: true });
  };

  const startCapture = (kind: CaptureMediaKind) => {
    keepFocusInCard();
    useCaptureStore.getState().startCapture(card, kind, ticketShareToken);
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const onFilePicked = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file again still fires a change.
    event.target.value = "";
    if (!file) {
      return;
    }
    keepFocusInCard();
    useCaptureStore.getState().uploadCaptureFile(card, file, ticketShareToken);
  };

  const skip = () => {
    keepFocusInCard();
    useCaptureStore.getState().skipRequest(card);
  };

  const cancel = () => {
    keepFocusInCard();
    useCaptureStore.getState().cancelCapture(card.id);
  };

  const tile = (
    kind: CaptureMediaKind | "upload",
    disabled: boolean,
    isBusy: boolean,
  ) => {
    const title =
      kind === "screenshot"
        ? t("captureTakeScreenshot")
        : kind === "recording"
          ? t("captureRecordScreen")
          : t("captureUploadFile");
    const hint =
      kind === "screenshot"
        ? t("captureScreenshotHint")
        : kind === "recording"
          ? fillCaptureText(t("captureRecordLimit"), {
              limit: formatCaptureLimit(limitSec),
            })
          : undefined;
    const icon =
      kind === "screenshot" ? (
        <CameraIcon />
      ) : kind === "recording" ? (
        <ScreenRecordIcon />
      ) : (
        <PaperclipIcon />
      );
    return (
      <button
        key={kind}
        type="button"
        className="capture-card-tile"
        disabled={disabled}
        aria-busy={isBusy || undefined}
        onClick={() =>
          kind === "upload" ? openFilePicker() : startCapture(kind)
        }
      >
        <span className="capture-card-tile-icon">
          {isBusy ? <Spinner /> : icon}
        </span>
        <span className="capture-card-tile-text">
          <span className="capture-card-tile-title">{title}</span>
          {hint && <span className="capture-card-tile-hint">{hint}</span>}
        </span>
      </button>
    );
  };

  const sendHostShot = () => {
    keepFocusInCard();
    useCaptureStore.getState().sendHostScreenshot(card, ticketShareToken);
  };

  const editHostShot = () => {
    keepFocusInCard();
    useCaptureStore.getState().editHostScreenshot(card, ticketShareToken);
  };

  // Kai intake (CONTRACT §3.2): the screen the native app took when the chat
  // opened, offered before anything else. Only an image the server can take
  // as an upload, and only a recent one.
  const offersHostShot =
    view.type === "pending" &&
    card.offerHostScreenshot === true &&
    !isHostShotDeclined &&
    !!hostScreenshot &&
    Date.now() - hostScreenshot.at < HOST_SCREENSHOT_MAX_AGE_MS &&
    (view.upload || view.uploadIsPrimary);

  const renderHostShot = () => {
    if (view.type !== "pending" || !hostScreenshot) {
      return null;
    }
    const isBusy = !!view.busy;
    return (
      <div className="capture-card-body">
        {view.issue && (
          <div className="capture-card-notice" role="alert">
            <AlertIcon />
            <span>{t(ISSUE_TEXT[view.issue])}</span>
          </div>
        )}
        <div className="capture-card-hostshot">
          <button
            type="button"
            className="capture-card-hostshot-img"
            aria-label={t("captureHostShotEdit")}
            disabled={isBusy || view.captureDisabled}
            onClick={editHostShot}
          >
            <img src={hostScreenshot.dataUrl} alt="" />
            {view.busy === "screenshot" && (
              <span className="capture-card-hostshot-busy">
                <Spinner />
              </span>
            )}
          </button>
          <span className="capture-card-hostshot-text">
            <span className="capture-card-tile-title">
              {t("captureHostShotTitle")}
            </span>
            <span className="capture-card-tile-hint">
              {t("captureHostShotHint")}
            </span>
          </span>
        </div>
        <div className="capture-card-hostshot-actions">
          <button
            type="button"
            className="cw-btn cw-btn--primary"
            disabled={isBusy}
            aria-busy={view.busy === "upload" || undefined}
            onClick={sendHostShot}
          >
            {view.busy === "upload" && <Spinner />}
            <span>{t("captureHostShotSend")}</span>
          </button>
          <button
            type="button"
            className="cw-btn cw-btn--ghost"
            disabled={isBusy}
            onClick={() => setHostShotDeclined(true)}
          >
            <span>{t("captureHostShotRetake")}</span>
          </button>
        </div>
        {view.skip && (
          <div className="capture-card-foot">
            <button
              type="button"
              className="capture-card-link capture-card-link--end"
              disabled={isBusy}
              aria-busy={view.busy === "skip" || undefined}
              onClick={skip}
            >
              {view.busy === "skip" && <Spinner />}
              <span>{t("captureSkip")}</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderPending = () => {
    if (view.type !== "pending") {
      return null;
    }
    if (offersHostShot) {
      return renderHostShot();
    }
    const isBusy = !!view.busy;
    const tiles: ReactNode[] = [];
    if (view.screenshot) {
      tiles.push(
        tile(
          "screenshot",
          isBusy || view.captureDisabled,
          view.busy === "screenshot",
        ),
      );
    }
    if (view.recording) {
      tiles.push(
        tile(
          "recording",
          isBusy || view.captureDisabled,
          view.busy === "recording",
        ),
      );
    }
    if (view.uploadIsPrimary) {
      tiles.push(tile("upload", isBusy, view.busy === "upload"));
    }

    return (
      <div className="capture-card-body">
        {view.issue && (
          <div className="capture-card-notice" role="alert">
            <AlertIcon />
            <span>{t(ISSUE_TEXT[view.issue])}</span>
          </div>
        )}
        {tiles.length > 0 && (
          <div
            className={`capture-card-tiles ${
              tiles.length === 1 ? "capture-card-tiles--single" : ""
            }`}
          >
            {tiles}
          </div>
        )}
        {((view.upload && !view.uploadIsPrimary) || view.skip) && (
          <div className="capture-card-foot">
            {view.upload && !view.uploadIsPrimary && (
              <button
                type="button"
                className="capture-card-link"
                disabled={isBusy}
                aria-busy={view.busy === "upload" || undefined}
                onClick={openFilePicker}
              >
                {view.busy === "upload" ? <Spinner /> : <PaperclipIcon />}
                <span>{t("captureUploadFile")}</span>
              </button>
            )}
            {view.skip && (
              <button
                type="button"
                className="capture-card-link capture-card-link--end"
                disabled={isBusy}
                aria-busy={view.busy === "skip" || undefined}
                onClick={skip}
              >
                {view.busy === "skip" && <Spinner />}
                <span>{t("captureSkip")}</span>
              </button>
            )}
          </div>
        )}
        {view.upload && (
          // Visually hidden, not display:none — some mobile WebViews won't
          // open the picker for an input that isn't rendered. The visible
          // "Upload a file" button above is what users (and AT) interact with.
          <input
            ref={fileInputRef}
            className="capture-card-file-input"
            type="file"
            accept={captureAcceptForKind(card.kind)}
            tabIndex={-1}
            aria-label={t("captureUploadFile")}
            onChange={onFilePicked}
          />
        )}
      </div>
    );
  };

  const meter = (progress?: number) => (
    <div className="capture-card-meter" aria-hidden="true">
      <span style={{ width: `${Math.round((progress ?? 0) * 100)}%` }} />
    </div>
  );

  const renderProgress = () => {
    if (view.type !== "progress") {
      return null;
    }
    const percent = Math.round((view.progress ?? 0) * 100);
    const notice = view.claimedElsewhere && (
      <div className="capture-card-notice capture-card-notice--info">
        <InfoIcon />
        <span>{t("captureClaimedElsewhere")}</span>
      </div>
    );

    // A file the customer picked: its row, with the upload's progress.
    if (view.label === "uploading" && view.fileName) {
      return (
        <>
          {notice}
          <div className="capture-card-file" role="status">
            <span className="capture-card-file-tile">
              <ImageFileIcon />
            </span>
            <span className="capture-card-file-text">
              <span className="capture-card-file-name">{view.fileName}</span>
              <span className="capture-card-file-meta capture-card-num">
                {fillCaptureText(t("captureUploadingPercent"), { percent })}
              </span>
            </span>
          </div>
          {meter(view.progress)}
        </>
      );
    }

    let lead: ReactNode = <Spinner />;
    let title: string;
    let sub: ReactNode;
    switch (view.label) {
      case "editing":
        title = t("captureEditingTitle");
        sub = t("captureEditingHint");
        break;
      case "recording": {
        const live =
          !view.paused && typeof view.elapsedAt === "number"
            ? (Date.now() - view.elapsedAt) / 1000
            : 0;
        const elapsed = Math.min(limitSec, (view.elapsedSec ?? 0) + live);
        lead = (
          <span
            className={`capture-card-recdot ${
              view.paused ? "capture-card-recdot--paused" : ""
            }`}
            aria-hidden="true"
          />
        );
        title = view.paused
          ? t("captureRecordingPaused")
          : t("captureBarRecording");
        sub = (
          <span className="capture-card-num">
            {fillCaptureText(t("captureRecordingOf"), {
              elapsed: formatCaptureElapsed(elapsed),
              limit: formatCaptureElapsed(limitSec),
            })}
          </span>
        );
        break;
      }
      case "preview":
        title = t("captureReviewTitle");
        sub = t("captureReviewHint");
        break;
      case "uploading":
        title = t("captureSendingTitle");
        sub = (
          <span className="capture-card-num">
            {fillCaptureText(t("captureUploadingPercent"), { percent })}
          </span>
        );
        break;
      default:
        if (view.kind === "recording") {
          title = t("captureReadyTitle");
          sub = t("captureReadyHint");
        } else {
          title = t("captureWaitingTitle");
          sub = t("captureWaitingHint");
        }
    }

    return (
      <>
        {notice}
        <div className="capture-card-wait" role="status">
          {lead}
          <span className="capture-card-wait-text">
            <span className="capture-card-wait-title">{title}</span>
            <span className="capture-card-wait-sub">{sub}</span>
          </span>
          {view.canCancel && (
            <button type="button" className="capture-card-link" onClick={cancel}>
              <span>{t("captureBarCancel")}</span>
            </button>
          )}
        </div>
        {view.label === "uploading" && meter(view.progress)}
      </>
    );
  };

  const sentBadge = (
    <span className="capture-card-ok" aria-hidden="true">
      <CheckIcon />
    </span>
  );

  const renderSent = () => {
    if (view.type !== "sent") {
      return null;
    }
    const justNow =
      typeof view.sentAt === "number" && Date.now() - view.sentAt < 60000;

    const resultFile = result?.attachments?.[0];
    const resultCapture = result?.data?.capture;

    // A picked file: a row with its name and size (until its message brings
    // the file to show here).
    if (view.sentVia === "upload" && view.fileName && !resultFile) {
      const size = formatCaptureFileSize(view.fileSize);
      const meta = [size, justNow ? t("captureJustNow").toLowerCase() : ""]
        .filter(Boolean)
        .join(" · ");
      return (
        <div className="capture-card-file" role="status">
          <span className="capture-card-file-tile">
            {view.thumbnailUrl ? (
              <img src={view.thumbnailUrl} alt="" />
            ) : (
              <ImageFileIcon />
            )}
          </span>
          <span className="capture-card-file-text">
            <span className="capture-card-file-name">{view.fileName}</span>
            {meta && <span className="capture-card-file-meta">{meta}</span>}
          </span>
          <span className="capture-card-sent">
            {sentBadge}
            <span>{t("captureSent")}</span>
          </span>
        </div>
      );
    }

    const resultIsImage = isCaptureResultImage(resultFile);
    const isRecording = resultFile
      ? !resultIsImage
      : view.sentKind === "recording";
    const thumbnailUrl =
      view.thumbnailUrl ??
      (resultIsImage
        ? attachmentDisplayUrl(resultFile, RESULT_THUMBNAIL_WIDTH)
        : undefined);
    const hasMedia = !!thumbnailUrl || isRecording;
    const isPageRecording = isCapturePageRecording(resultCapture, resultFile);
    // The recording plays in the card once the flight has landed. A page
    // recording has no video: it keeps the recording tile.
    const playsInline =
      isRecording && !!resultFile && !isPageRecording && !view.flyPending;
    const annotated =
      view.annotated ??
      (resultCapture && resultIsImage
        ? Array.isArray(resultCapture.annotations) &&
          resultCapture.annotations.length > 0
        : undefined);
    const durationSec =
      view.durationSec ??
      (typeof resultCapture?.durationMs === "number"
        ? Math.round(resultCapture.durationMs / 1000)
        : undefined);
    const detail = isRecording
      ? typeof view.audio === "boolean"
        ? t(view.audio ? "captureSentWithAudio" : "captureSentNoAudio")
        : undefined
      : typeof annotated === "boolean"
        ? t(annotated ? "captureSentMarkedUp" : "captureSentAsCaptured")
        : undefined;
    const openImage =
      resultIsImage && resultFile?.url
        ? () => {
            if (isApp) {
              Communicator.openURL(resultFile.url, false, { placeholders: false });
              return;
            }
            Communicator.openImage(
              attachmentDisplayUrl(resultFile, RESULT_FULLSCREEN_WIDTH)!,
            );
          }
        : undefined;

    return (
      <>
        {hasMedia && (
          <div
            ref={slotRef}
            className={`capture-card-media ${
              view.flyPending ? "capture-card-media--waiting" : ""
            } ${thumbnailUrl ? "" : "capture-card-media--empty"} ${
              playsInline ? "capture-card-media--player" : ""
            }`}
          >
            {playsInline ? (
              <MessageVideoPlayer
                attachment={{ ...resultFile!, posterUrl: thumbnailUrl }}
                fallback={thumbnailUrl ? <img src={thumbnailUrl} alt="" /> : null}
              />
            ) : (
              <>
                {thumbnailUrl ? <img src={thumbnailUrl} alt="" /> : null}
                {openImage && (
                  <button
                    type="button"
                    className="capture-card-media-open"
                    aria-label={resultFile?.name || t("captureScreenshotLabel")}
                    onClick={openImage}
                  />
                )}
                {isRecording && (
                  <span className="capture-card-play" aria-hidden="true">
                    <span>
                      <PlayIcon />
                    </span>
                  </span>
                )}
                <span className="capture-card-chip">
                  {isRecording ? <PlayIcon /> : <CameraIcon />}
                  <span className="capture-card-num">
                    {isRecording
                      ? typeof durationSec === "number"
                        ? formatCaptureElapsed(durationSec)
                        : isPageRecording
                          ? t("capturePageRecording")
                          : t("captureRecordingLabel")
                      : t("captureScreenshotLabel")}
                  </span>
                </span>
              </>
            )}
          </div>
        )}
        <div className="capture-card-meta" role="status">
          {sentBadge}
          <b>{t("captureSent")}</b>
          {detail && <span className="capture-card-meta-mut">· {detail}</span>}
          {justNow && (
            <span className="capture-card-meta-mut capture-card-meta-end">
              {t("captureJustNow")}
            </span>
          )}
        </div>
      </>
    );
  };

  const renderReceipt = () => {
    if (
      view.type !== "skipped" &&
      view.type !== "expired" &&
      view.type !== "cancelled"
    ) {
      return null;
    }
    if (view.type === "skipped") {
      return (
        <div className="capture-card-receipt" role="status">
          <b>{t("captureSkipped")}</b>
          <span className="capture-card-receipt-detail">
            · {t("captureSkippedDetail")}
          </span>
          {openComposerPicker && (
            <button
              type="button"
              className="capture-card-link"
              onClick={() => openComposerPicker()}
            >
              <span>{t("captureShareOne")}</span>
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="capture-card-receipt" role="status">
        <InfoIcon />
        <span>
          {t(view.type === "expired" ? "captureExpired" : "captureCancelled")}
        </span>
      </div>
    );
  };

  const isReceipt =
    view.type === "skipped" ||
    view.type === "expired" ||
    view.type === "cancelled";

  // The group's accessible name follows the card's state: Kai's ask while it
  // waits, then what actually happened (never "Waiting…" on a sent card).
  const groupLabel = (() => {
    switch (view.type) {
      case "sent": {
        const sentFile = result?.attachments?.[0];
        const isRecording = sentFile
          ? !isCaptureResultImage(sentFile)
          : view.sentKind
            ? view.sentKind === "recording"
            : undefined;
        if (isRecording === undefined) return t("captureSent");
        return `${t(
          isRecording ? "captureRecordingLabel" : "captureScreenshotLabel",
        )} · ${t("captureSent")}`;
      }
      case "skipped":
        return `${t("captureSkipped")} · ${t("captureSkippedDetail")}`;
      case "expired":
        return t("captureExpired");
      case "cancelled":
        return t("captureCancelled");
      default:
        return message || t("captureWaitingTitle");
    }
  })();

  return (
    <div
      ref={rootRef}
      className={[
        "cw",
        "capture-card",
        `capture-card--${variant}`,
        isReceipt ? "capture-card--receipt" : "",
        swapped ? "capture-card--swap" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="group"
      aria-label={groupLabel}
      tabIndex={-1}
      data-capture-request={card.id}
      data-capture-view={viewKey}
    >
      {variant === "step" && message && (
        <div className="capture-card-message">{message}</div>
      )}
      {isReceipt ? (
        renderReceipt()
      ) : (
        <div className="capture-card-surface" key={viewKey}>
          {renderPending()}
          {renderProgress()}
          {renderSent()}
        </div>
      )}
    </div>
  );
};
